import {
  Inject,
  Injectable,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { UserProfile } from '../auth/models/user-profile.model';
import { EnvironmentInterface } from '../models/environment.interface';
import { ENVIRONMENT } from '../models/environment.token';
import { QnSbnService } from '../../features/qn-sbn-eservice/services/qn-sbn-service.service';

import { EnvConfigService } from './env-config.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  constructor(
    private readonly authService: AuthService,
    private readonly envConfigService: EnvConfigService,
    private readonly qnSbnService: QnSbnService,
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentInterface,
  ) {}

  initApp(): Observable<UserProfile> {
    this.setAppUrls();

    return this.envConfigService.getEnvironmentConfiguration().pipe(
      switchMap(config => this.authService.init(config)),
      switchMap(userProfile => {
        if (userProfile) {
          localStorage.setItem('name', userProfile.name ?? '');
          localStorage.setItem('email', userProfile.email ?? '');
          localStorage.setItem('initials', userProfile.name[0] ?? '');

          // Ensure QnSbnService has the correct baseUrl before calling the API
          this.qnSbnService.baseUrl = this.environment.appApiUrl;

          // Fetch user details (countries & permissions) at startup
          // so they are available to all components immediately.
          return this.qnSbnService.qnSbnNotificationInitCall().pipe(
            tap(userInfo => {
              if (userInfo) {
                this.qnSbnService.setUserData(userInfo);
              }
            }),
            switchMap(() => [userProfile]),
            catchError(err => {
              console.warn('Failed to load user details at startup:', err);
              // Don't block app startup if userdetails call fails
              return of(userProfile);
            }),
          );
        } else {
          // No user. Abort start of the application and redirect to login
          return throwError(
            () => new Error('Initialization failed: UserProfile is null'),
          );
        }
      }),
      catchError(error => {
        return throwError(() => error);
      }),
    );
  }

  private setAppUrls(): void {
    const appUrl = window.location.origin;
    this.environment.appUrl = appUrl;
    this.environment.appApiUrl = `${appUrl}/api/`;
    this.environment.apiUrl = `${appUrl}/sandbox`;
  }
}

export const appInitializerProvider = provideAppInitializer(() => {
  const initializerFn = (
    (
      appInitializerService: AppInitializerService,
    ): (() => Observable<UserProfile>) =>
    () =>
      appInitializerService.initApp()
  )(inject(AppInitializerService));
  return initializerFn();
});
